import { useCallback, useEffect, useRef, useState } from 'react'
// NOTE: unfortunately without this polyfill scrollIntoViewIfNeeded doesn't work correctly in Firefox
// and have minor issues in Chrome in Safari
import scrollIntoView from 'scroll-into-view-if-needed'

const DATA_ROLE = 'slider_item'

export const useSlider = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const sliderRef = useRef<HTMLDivElement>(null)
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null)
  const [allSlidesVisibility, setAllSlidesVisibility] = useState<Record<string, boolean>>({})
  const [partiallyVisibleSlides, setPartiallyVisibleSlides] = useState<Record<string, boolean>>({})
  const [isScrolling, setIsScrolling] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const slides = Object.entries(allSlidesVisibility).flatMap(([, value]) => value)
  const partialSlides = Object.entries(partiallyVisibleSlides).flatMap(([, value]) => value)

  const firstVisibleSlideIndex = slides.indexOf(true)
  const lastVisibleSlideIndex = slides.lastIndexOf(true)
  const lastPartiallyVisibleIndex = partialSlides.lastIndexOf(true)

  // Check if all slides are fully visible
  const allSlidesFullyVisible = slides.length > 0 && slides.filter(Boolean).length === slides.length

  // Check if the last slide is partially visible but not fully visible
  const lastSlidePartiallyVisible =
    lastPartiallyVisibleIndex === slides.length - 1 && lastVisibleSlideIndex !== slides.length - 1

  const goPrevEnabled = firstVisibleSlideIndex > 0 && !isScrolling
  const goNextEnabled =
    !isScrolling &&
    ((lastVisibleSlideIndex !== -1 && lastVisibleSlideIndex < slides.length - 1) ||
      lastSlidePartiallyVisible) &&
    !allSlidesFullyVisible

  const getSliderItems = useCallback(() => {
    if (!sliderRef.current) return []

    return Array.from(sliderRef.current.querySelectorAll<HTMLElement>(`[data-role="${DATA_ROLE}"]`))
  }, [])

  const visibleSlidesCount = useCallback(() => {
    const items = getSliderItems()

    return items.length
  }, [getSliderItems])

  const resetSliderCalculations = useCallback(() => {
    setAllSlidesVisibility({})
    setPartiallyVisibleSlides({})
    setRefreshKey((prev) => prev + 1)
  }, [])

  useEffect(() => {
    if (!containerRef.current || !sliderRef.current) return

    const items = getSliderItems()

    // Observer with high threshold for full visibility
    const handleIntersectFull = (entries: IntersectionObserverEntry[]) => {
      setAllSlidesVisibility((prevState) => {
        const newState = { ...prevState }

        entries.forEach((e) => {
          const index = items.findIndex((el) => el === e.target)

          if (index !== -1) {
            newState[index] = e.isIntersecting
          }
        })

        return newState
      })
    }

    // Observer with low threshold for partial visibility
    const handleIntersectPartial = (entries: IntersectionObserverEntry[]) => {
      setPartiallyVisibleSlides((prevState) => {
        const newState = { ...prevState }

        entries.forEach((e) => {
          const index = items.findIndex((el) => el === e.target)

          if (index !== -1) {
            newState[index] = e.isIntersecting
          }
        })

        return newState
      })
    }

    const fullObserver = new IntersectionObserver(handleIntersectFull, {
      root: containerRef.current,
      rootMargin: '0px',
      threshold: 0.95, // Less restrictive threshold for full visibility
    })

    const partialObserver = new IntersectionObserver(handleIntersectPartial, {
      root: containerRef.current,
      rootMargin: '50px', // Additional margin to detect elements approaching the viewport
      threshold: 0.05, // Requires minimal visibility
    })

    items.forEach((el) => {
      fullObserver.observe(el)
      partialObserver.observe(el)
    })

    // Add listener for scroll end event
    const scrollEndHandler = () => {
      setIsScrolling(false)
    }

    // Handler for browsers that support scrollend
    if ('onscrollend' in window) {
      containerRef.current.addEventListener('scrollend', scrollEndHandler)
    }

    // Fallback for browsers without scrollend support
    const scrollHandler = () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current)
      }

      scrollTimeout.current = setTimeout(() => {
        setIsScrolling(false)
      }, 150) // shorter timeout for better responsiveness
    }

    containerRef.current.addEventListener('scroll', scrollHandler)

    return () => {
      fullObserver.disconnect()
      partialObserver.disconnect()

      if (containerRef.current) {
        if ('onscrollend' in window) {
          containerRef.current.removeEventListener('scrollend', scrollEndHandler)
        }
        containerRef.current.removeEventListener('scroll', scrollHandler)
      }

      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current)
      }
    }
  }, [containerRef, getSliderItems, sliderRef, refreshKey])

  const setActiveSlide = useCallback(
    (index: number) => {
      const items = getSliderItems()
      const targetItem = items[index]

      if (!targetItem?.isConnected) return

      setIsScrolling(true)

      try {
        scrollIntoView(targetItem, {
          behavior: 'smooth',
          block: 'nearest',
          scrollMode: 'always', // Always scroll instead of "if-needed"
          inline: 'center', // Center the element instead of aligning to start
          boundary: containerRef.current, // Respect the container boundaries
        })

        // Reset isScrolling after animation completes
        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current)
        }

        scrollTimeout.current = setTimeout(() => {
          setIsScrolling(false)
        }, 300) // shorter timeout for better responsiveness
      } catch (error) {
        setIsScrolling(false)
      }
    },
    [getSliderItems],
  )

  const goPrev = () => {
    if (!goPrevEnabled) return
    setActiveSlide(Math.max(0, firstVisibleSlideIndex - 1))
  }

  const goNext = () => {
    if (!goNextEnabled) return

    // Find the next slide that should be made visible
    const nextVisibleIndex = lastVisibleSlideIndex + 1

    // If we're at the end, and there's a partially visible slide, make that one fully visible
    if (nextVisibleIndex >= slides.length && lastSlidePartiallyVisible) {
      setActiveSlide(slides.length - 1)

      return
    }

    // Otherwise go to the next slide
    setActiveSlide(Math.min(slides.length - 1, nextVisibleIndex))
  }

  return {
    dataRole: DATA_ROLE,
    getContainerProps: () => ({
      ref: containerRef,
      $slidesCount: visibleSlidesCount(),
    }),
    getSliderProps: () => ({ ref: sliderRef }),
    getSlideProps: () => ({ 'data-role': DATA_ROLE }),
    getPrevButtonProps: () => ({ disabled: !goPrevEnabled, onClick: goPrev }),
    getNextButtonProps: () => ({ disabled: !goNextEnabled, onClick: goNext }),
    resetSlider: resetSliderCalculations,
  }
}
